import styled, { css } from 'styled-components';

// ---------------------------------------------------------------------------
// Régua de Cobrança — primitivos base (handoff de design "Cobranças Vencidas").
//
// Mesmo design system do dashboard de Vendas a prazo (0084): superfícies
// brancas raio 12 sobre o shell escuro, controles 40px raio 10, sombra única
// `0 1px 2px rgba(0,0,0,0.04)`, transições só de cor/borda em 120–160ms.
// Onde o design fixa valor que não é token do tema, o literal mora AQUI, num
// lugar só — não espalhado pelos componentes.
//
// Botões: o UI_SPEC da tela usa o primário claro (#96c4f0/texto #0b3a63) do
// protótipo — mesma decisão do 0084. Os botões de shell escuro são caso que a
// FC7 §G.2 mantém explicitamente local (o kit `styles/ui/Button.js` é para
// superfície clara padrão do app).
// ---------------------------------------------------------------------------

export const CARD_BORDER = 'rgba(0, 0, 0, 0.08)';
export const CONTROL_BORDER = 'rgba(0, 0, 0, 0.12)';
export const CARD_SHADOW = '0 1px 2px rgba(0, 0, 0, 0.04)';
export const FOCUS_RING = '0 0 0 3px rgba(64, 152, 239, 0.18)';

// Números nunca quebram linha e usam algarismos de largura fixa.
export const numericCss = css`
   font-variant-numeric: tabular-nums;
   white-space: nowrap;
`;

export const focusableCss = css`
   &:focus-visible {
      outline: none;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#4098ef'};
      box-shadow: ${FOCUS_RING};
   }
`;

// ─── Superfícies ──────────────────────────────────────────────────────────

export const Card = styled.section`
   background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};
   border: 1px solid ${CARD_BORDER};
   border-radius: 12px;
   box-shadow: ${CARD_SHADOW};
   overflow: hidden;
`;

export const CardHead = styled.div`
   padding: 14px 16px;
   border-bottom: 1px solid rgba(0, 0, 0, 0.07);
   display: flex;
   align-items: center;
   gap: 10px;
   flex-wrap: wrap;
`;

export const CardTitle = styled.b`
   font-size: 15px;
   font-weight: 700;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

export const CardHint = styled.span`
   font-size: 12px;
   color: #8a8a8a;
`;

// ─── Pills ────────────────────────────────────────────────────────────────
// Raio 999, 10px/800, fundo translúcido + texto escuro da mesma matiz — nunca
// preenchimento sólido (UI_SPEC §Pills).

const PILL_VARIANTS = {
   neutral: { bg: 'rgba(0, 0, 0, 0.06)', fg: '#555' },
   info: { bg: 'rgba(64, 152, 239, 0.12)', fg: '#0b3a63' },
   warn: { bg: 'rgba(245, 158, 11, 0.16)', fg: '#8a5a00' },
   alert: { bg: 'rgba(234, 88, 12, 0.14)', fg: '#9a3d00' },
   critical: { bg: 'rgba(220, 38, 38, 0.12)', fg: '#a01b1b' },
   success: { bg: 'rgba(22, 163, 74, 0.12)', fg: '#15803d' },
   reopened: { bg: 'rgba(147, 51, 234, 0.12)', fg: '#6b21a8' },
   // Pill sobre o cabeçalho escuro do drawer (raia atual).
   onDark: { bg: 'rgba(255, 255, 255, 0.14)', fg: '#fff' },
   // Raia terminal (Recebido) sobre o cabeçalho escuro: verde legível no
   // fundo #373737 — o translúcido claro do kit sumiria ali.
   successOnDark: { bg: 'rgba(22, 163, 74, 0.35)', fg: '#bbf7d0' },
   // Dado ausente ("—", sem título aberto).
   muted: { bg: 'rgba(0, 0, 0, 0.05)', fg: '#9a9a9a' },
};

export const Pill = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 4px;
   font-size: 10px;
   font-weight: 800;
   padding: 3px 8px;
   border-radius: 999px;
   white-space: nowrap;
   background: ${({ $variant }) =>
      (PILL_VARIANTS[$variant] ?? PILL_VARIANTS.neutral).bg};
   color: ${({ $variant }) =>
      (PILL_VARIANTS[$variant] ?? PILL_VARIANTS.neutral).fg};

   svg {
      flex-shrink: 0;
   }
`;

// Chip de tratativas com contagem zero: branco de borda tracejada.
export const DashedPill = styled(Pill)`
   background: #fff;
   color: #9a9a9a;
   border: 1px dashed rgba(0, 0, 0, 0.2);
`;

// ─── Tabela de cabeçalho escuro (fila + títulos do caso) ──────────────────

export const TableScroll = styled.div`
   overflow-x: auto;
`;

export const DarkTable = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 12px;

   thead tr {
      background: ${({ theme }) => theme?.colors?.appBackground ?? '#373737'};
   }

   th {
      padding: 9px 12px;
      text-align: left;
      font-size: 11px;
      font-weight: 800;
      color: #fff;
      white-space: nowrap;

      &.num {
         text-align: right;
      }
   }

   tbody tr {
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
   }

   td {
      padding: 9px 12px;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};

      &.num {
         text-align: right;
      }
   }
`;

// Linha inteira clicável da fila. Hover azul claro do design (affordance de
// linha; o gesto — clique simples abre o drawer — é da tela).
export const ClickableTr = styled.tr`
   cursor: pointer;
   transition: background 120ms ease;

   &:hover {
      background: ${({ theme }) => theme?.colors?.primarySoft ?? '#96c4f0'};
   }
`;

// ─── Controles de formulário (40px / raio 10) ─────────────────────────────

export const FieldLabel = styled.label`
   font-size: 12px;
   font-weight: 700;
   color: #666;
   display: block;
   margin-bottom: 6px;
`;

export const FieldHint = styled.div`
   font-size: 11px;
   color: #9a9a9a;
   margin-top: 6px;
   line-height: 1.5;
`;

export const ControlInput = styled.input`
   height: 40px;
   width: 100%;
   border: 1px solid ${CONTROL_BORDER};
   border-radius: 10px;
   padding: 0 12px;
   font: inherit;
   font-size: 14px;
   background: #fff;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   transition: border-color 140ms ease, box-shadow 140ms ease;

   &:focus {
      outline: none;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#4098ef'};
      box-shadow: ${FOCUS_RING};
   }
`;

export const ControlSelect = styled.select`
   height: 40px;
   width: 100%;
   border: 1px solid ${CONTROL_BORDER};
   border-radius: 10px;
   padding: 0 10px;
   font: inherit;
   font-size: 14px;
   background: #fff;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   transition: border-color 140ms ease, box-shadow 140ms ease;

   &:focus {
      outline: none;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#4098ef'};
      box-shadow: ${FOCUS_RING};
   }
`;

export const ControlTextarea = styled.textarea`
   width: 100%;
   border: 1px solid ${CONTROL_BORDER};
   border-radius: 10px;
   padding: 10px 12px;
   font: inherit;
   font-size: 14px;
   resize: vertical;
   background: #fff;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   transition: border-color 140ms ease, box-shadow 140ms ease;

   &:focus {
      outline: none;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#4098ef'};
      box-shadow: ${FOCUS_RING};
   }
`;

// ─── Botões da superfície clara ───────────────────────────────────────────
// ⚠️ G.4.1: todo styled.button declara o próprio :hover COM background, senão o
// hover azul do GlobalStyles legado vaza por especificidade.

const buttonBase = css`
   height: 40px;
   padding: 0 16px;
   border-radius: 10px;
   font-family: inherit;
   font-size: 14px;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 8px;
   white-space: nowrap;
   transition: background 140ms ease, border-color 140ms ease;

   &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }

   svg {
      flex-shrink: 0;
   }
`;

// Primário do design (azul claro, texto azul-escuro). O #4098ef saturado fica
// para foco/link/ícone ativo — nunca fundo de botão nesta tela (UI_SPEC).
export const PrimaryActionButton = styled.button`
   ${buttonBase};
   border: none;
   background: ${({ theme }) => theme?.colors?.primarySoft ?? '#96c4f0'};
   color: #0b3a63;
   font-weight: 700;

   &:hover:not(:disabled) {
      background: #8bbcee;
   }
`;

export const SecondaryButton = styled.button`
   ${buttonBase};
   border: 1px solid ${CONTROL_BORDER};
   background: #fff;
   color: #444;
   font-weight: 600;

   &:hover:not(:disabled) {
      background: #f4f4f5;
   }
`;

// Destrutivo do design: fundo translúcido vermelho, sem sólido.
export const DestructiveButton = styled.button`
   ${buttonBase};
   border: 1px solid rgba(220, 38, 38, 0.4);
   background: rgba(220, 38, 38, 0.06);
   color: #a01b1b;
   font-weight: 700;
   font-size: 13px;

   &:hover:not(:disabled) {
      background: rgba(220, 38, 38, 0.12);
   }
`;

// Ação de linha 30×30 (boleto/PIX/baixa nas tabelas de título).
export const RowIconButton = styled.button`
   width: 30px;
   height: 30px;
   flex: 0 0 30px;
   border-radius: 8px;
   border: 1px solid ${CONTROL_BORDER};
   background: #fff;
   color: #555;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-family: inherit;
   transition: background 120ms ease, border-color 120ms ease, color 120ms ease;

   &:hover:not(:disabled) {
      background: #f3f8fe;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#4098ef'};
      color: #1f66bd;
   }

   &:disabled {
      cursor: not-allowed;
      border-color: rgba(0, 0, 0, 0.08);
      background: #fafafa;
      color: #c9c9c9;
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }
`;

// Variante "dar baixa" (verde) — segue desabilitável em título encerrado ou
// aguardando webhook.
export const RowSettleButton = styled(RowIconButton)`
   border-color: rgba(22, 163, 74, 0.35);
   background: rgba(22, 163, 74, 0.08);
   color: #15803d;

   &:hover:not(:disabled) {
      background: rgba(22, 163, 74, 0.16);
      border-color: rgba(22, 163, 74, 0.5);
      color: #15803d;
   }

   &:disabled {
      border-color: rgba(0, 0, 0, 0.08);
      background: #fafafa;
      color: #c9c9c9;
   }
`;

// Mesma âncora visual do RowIconButton para o link do boleto (<a> real: abre em
// nova aba, acessível — FC7 G.3).
export const RowIconLink = styled.a`
   width: 30px;
   height: 30px;
   flex: 0 0 30px;
   border-radius: 8px;
   border: 1px solid ${CONTROL_BORDER};
   background: #fff;
   color: #555;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   transition: background 120ms ease, border-color 120ms ease, color 120ms ease;

   &:hover {
      background: #f3f8fe;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#4098ef'};
      color: #1f66bd;
      text-decoration: none;
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }
`;

// ─── Estados vazios / erro ────────────────────────────────────────────────

export const EmptyWrap = styled.div`
   padding: 44px 16px;
   text-align: center;
`;

export const EmptyTitle = styled.div`
   font-size: 15px;
   font-weight: 700;
   margin-bottom: 4px;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

export const EmptyText = styled.div`
   font-size: 13px;
   color: #8a8a8a;
`;

// ─── Skeleton (carregando na forma final, sem spinner central) ────────────

export const SkeletonBlock = styled.div`
   border-radius: ${({ $radius }) => $radius ?? '10px'};
   height: ${({ $h }) => $h ?? '14px'};
   width: ${({ $w }) => $w ?? '100%'};
   background: rgba(0, 0, 0, 0.07);
   animation: dunningPulse 1.2s ease-in-out infinite;

   @keyframes dunningPulse {
      0%,
      100% {
         opacity: 1;
      }
      50% {
         opacity: 0.45;
      }
   }
`;
